import Logo from "@/components/logo/Logo";
import { useUser } from "@/contexts/UserContext";
import { SIDEBAR_MENU } from "@/utils/constants";
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Drawer, DrawerContent, DrawerTrigger, DrawerHeader } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";

export const SIDEBAR_SHRINK_PREFERENCE_KEY = "sidebar-shrink-preference";

export const SidebarShrinkContext = createContext<{
  shrinked: boolean;
  setShrinked: (state: boolean) => void;
}>({
  shrinked: false,
  setShrinked: () => {},
});

type SideBarProps =
  | {
      shrinked?: false;
      shrinkable?: false;
      setShrinked?: undefined;
    }
  | {
      shrinked: boolean;
      shrinkable: true;
      setShrinked: (state: boolean) => void;
    };

const StatelessSidebar = ({
  shrinked = false,
  shrinkable = false,
  setShrinked,
}: SideBarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useUser();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [selectedParent, setSelectedParent] = useState<string>("dashboard");
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [sidebarHeight, setSidebarHeight] = useState("100vh");

  const [featureState, setFeatureState] = useState({
    delivery_feature: false,
    loyalty_program_enabled: false,
    upsell_feature: false,
  });

  useEffect(() => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const parentPath = pathParts[0];
    const childPath = pathParts[1];

    const parentMenu = SIDEBAR_MENU.find((menu) => menu.name === parentPath);
    if (parentMenu) {
      setSelectedParent(parentMenu.name);
      if (
        childPath &&
        parentMenu.children.some((child) => child.name === childPath)
      ) {
        setSelectedChild(childPath);
        setOpenSubMenu(parentMenu.name);
      } else {
        setSelectedChild(null);
      }
    } else {
      setSelectedParent("dashboard");
      setSelectedChild(null);
    }
  }, [location]);

  useEffect(() => {
    if (user) {
      setFeatureState({
        delivery_feature: user.selectedBranch?.delivery_feature || false,
        loyalty_program_enabled:
          user.selectedBranch?.loyalty_program_enabled || false,
        upsell_feature: user.selectedBranch?.upsell_feature || false,
      });
    }
  }, [user]);

  useEffect(() => {
    const updateSidebarHeight = () => {
      setSidebarHeight(`${window.innerHeight}px`);
    };

    window.addEventListener("resize", updateSidebarHeight);
    updateSidebarHeight();

    return () => window.removeEventListener("resize", updateSidebarHeight);
  }, []);

  const handleMenuItemClick = (
    componentName: string,
    isChild: boolean = false,
    parentName: string | null = null,
  ) => {
    if (isChild) {
      setSelectedChild(componentName);
      if (parentName) {
        setSelectedParent(parentName);
      }
      // Close mobile sidebar when selecting a child item
      if (shrinkable && setShrinked) {
        setShrinked(!shrinked);
      }
    } else {
      setSelectedParent(componentName);
      setSelectedChild(null);
      setOpenSubMenu(null); // Close any open submenu when clicking a parent without children
      // Close mobile sidebar when selecting a parent item
      if (shrinkable && setShrinked) {
        setShrinked(!shrinked);
      }
    }
  };

  const handleToggleSubMenu = (subMenu: string) => {
    // If clicking a different submenu, close current and open new one
    setOpenSubMenu(openSubMenu === subMenu ? null : subMenu);
  };

  return (
    <aside
      className={`h-screen bg-white text-black overflow-y-auto ${
        shrinkable ? "w-full" : "w-64 fixed left-0 top-0 z-40"
      }`}
      style={{ 
        height: sidebarHeight,
      }}
      aria-label="Sidebar"
    >
      <div className="flex h-full flex-col">
        <div className="mb-5 flex justify-center p-2.5">
          <Logo size="h-12" />
        </div>

        {SIDEBAR_MENU.map((menu, index) => {
          if (
            menu.name === "loyalty" &&
            !featureState.loyalty_program_enabled
          ) {
            return null;
          }

          const IconComponent = menu.icon;
          const isParentSelected = selectedParent === menu.name;
          const hasSelectedChild =
            selectedChild &&
            menu.children.some((child) => child.name === selectedChild);

          return (
            <div key={`${menu.name}-${index}`}>
              {menu.children.length === 0 ? (
                <Link
                  to={`/${menu.name}`}
                  onClick={() => {
                    handleMenuItemClick(menu.name);
                  }}
                  className={`mb-2 flex w-full cursor-pointer items-center p-2.5 pl-4 pr-10 transition-colors duration-200 ease-in-out ${
                    isParentSelected
                      ? "border-l-4 border-l-red-500 bg-gray-200 text-gray-800"
                      : ""
                  }`}
                >
                  <div className="mr-2.5 flex h-5 w-5 items-center justify-center">
                    <IconComponent className="h-7 w-7" />
                  </div>
                  <span>{t(menu.title)}</span>
                </Link>
              ) : (
                <>
                  <div
                    onClick={() => handleToggleSubMenu(menu.name)}
                    className={`mb-2 flex w-full cursor-pointer items-center justify-between p-2.5 pl-4 pr-10 transition-colors duration-200 ease-in-out ${
                      openSubMenu === menu.name ||
                      isParentSelected ||
                      hasSelectedChild
                        ? "bg-gray-200 text-gray-800"
                        : ""
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="mr-2.5 flex h-5 w-5 items-center justify-center">
                        <IconComponent className="h-7 w-7" />
                      </div>
                      <span>{t(menu.title)}</span>
                    </div>
                    {openSubMenu === menu.name ? (
                      <ChevronUpIcon className="h-4 w-4 text-black" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-black" />
                    )}
                  </div>
                  <div
                    className={`transition-max-height overflow-hidden ${
                      openSubMenu === menu.name
                        ? "max-h-96 duration-700"
                        : "max-h-0 duration-300"
                    } ease-in-out`}
                  >
                    {menu.children.map((child, index) => {
                      if (
                        child.name === "upsell" &&
                        !featureState.upsell_feature
                      ) {
                        return null;
                      }
                      return (
                        <Link
                          to={`/${menu.name}/${child.name}`}
                          key={`${child.name}-${index}`}
                          onClick={() =>
                            handleMenuItemClick(child.name, true, menu.name)
                          }
                          className={`mb-2 flex w-full cursor-pointer items-center px-4 py-2 pl-11 text-sm font-medium transition-colors duration-200 ease-in-out hover:bg-gray-100 ${
                            selectedChild === child.name
                              ? "border-l-4 border-l-red-500 bg-gray-200 pl-[39px] text-gray-800"
                              : ""
                          }`}
                        >
                          {t(child.title)}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export const DesktopSidebar = () => {
  return <StatelessSidebar />;
};

export const MobileSidebar = () => {
  const { t } = useTranslation();
  const { shrinked, setShrinked } = useContext(SidebarShrinkContext);
  const [open, setOpen] = useState(false);
  const [activeParent, setActiveParent] = useState<string | null>(null);
  const navigate = useLocation();
  const { user } = useUser();
  const [featureState, setFeatureState] = useState({
    delivery_feature: false,
    loyalty_program_enabled: false,
    upsell_feature: false,
  });

  useEffect(() => {
    if (user) {
      setFeatureState({
        delivery_feature: user.selectedBranch?.delivery_feature || false,
        loyalty_program_enabled:
          user.selectedBranch?.loyalty_program_enabled || false,
        upsell_feature: user.selectedBranch?.upsell_feature || false,
      });
    }
  }, [user]);

  // Close drawer when a menu item is clicked
  const handleDrawerClose = () => {
    setOpen(false);
    setShrinked(false);
    setActiveParent(null);
  };

  const handleParentClick = (menuName: string) => {
    // Check if this menu has children
    const menu = SIDEBAR_MENU.find(m => m.name === menuName);
    if (menu && menu.children.length > 0) {
      setActiveParent(prevState => prevState === menuName ? null : menuName);
    } else {
      // If no children, navigate directly and close the drawer
      handleDrawerClose();
    }
  };

  // If we're showing a sub-menu
  if (activeParent) {
    const parentMenu = SIDEBAR_MENU.find(m => m.name === activeParent);
    if (!parentMenu) return null;

    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button 
            variant="outline" 
            size="icon" 
            aria-label="Open menu"
            className="fixed left-4 top-4 z-50 2xl:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="h-[80vh] max-h-[600px]">
          <DrawerHeader className="pb-0 pt-2 border-b flex flex-row items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setActiveParent(null)} 
              className="absolute left-2"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <div className="flex-1 text-center font-semibold">
              {t(parentMenu.title)}
            </div>
          </DrawerHeader>
          <div className="overflow-y-auto p-3">
            <div className="space-y-1">
              {parentMenu.children.map((child, index) => {
                if (
                  child.name === "upsell" &&
                  !featureState.upsell_feature
                ) {
                  return null;
                }
                return (
                  <Link
                    key={`submenu-${child.name}-${index}`}
                    to={`/${parentMenu.name}/${child.name}`}
                    className="flex items-center p-3 rounded-lg hover:bg-gray-100"
                    onClick={handleDrawerClose}
                  >
                    <span className="text-base">{t(child.title)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Main menu view
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          aria-label="Open menu"
          className="fixed left-4 top-4 z-50 2xl:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="h-[80vh] max-h-[600px]">
        <DrawerHeader className="pb-0 pt-2 border-b">
          <div className="flex justify-center">
            <Logo size="h-12" />
          </div>
        </DrawerHeader>
        <div className="overflow-y-auto px-1 pb-4 pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SIDEBAR_MENU.map((menu, index) => {
              if (
                menu.name === "loyalty" &&
                !featureState.loyalty_program_enabled
              ) {
                return null;
              }

              const IconComponent = menu.icon;
              const hasChildren = menu.children.length > 0;
              
              if (hasChildren) {
                // For items with children, show as a div that opens the submenu
                return (
                  <div
                    key={`mobile-${menu.name}-${index}`}
                    onClick={() => handleParentClick(menu.name)} 
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                      <IconComponent className="h-6 w-6 text-red-500" />
                    </div>
                    <div className="flex items-center">
                      <span className="text-center text-sm font-medium">{t(menu.title)}</span>
                      <ChevronRightIcon className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                );
              } else {
                // For items without children, use a Link
                return (
                  <Link
                    key={`mobile-${menu.name}-${index}`}
                    to={`/${menu.name}`}
                    onClick={handleDrawerClose}
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                      <IconComponent className="h-6 w-6 text-red-500" />
                    </div>
                    <span className="text-center text-sm font-medium">{t(menu.title)}</span>
                  </Link>
                );
              }
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
